import { Either, Schema } from "effect";

export type RuntimePublicEnvKey =
	| `BUN_PUBLIC_${string}`
	| `PUBLIC_${string}`
	| "PAYPAL_CLIENT_ID";

export type RuntimePublicEnv = Partial<Record<RuntimePublicEnvKey, string>>;

export const EMPTY_RUNTIME_PUBLIC_ENV = Object.freeze(
	Object.create(null) as RuntimePublicEnv,
) as Readonly<RuntimePublicEnv>;

export function isObjectRecord(value: unknown): value is object {
	return typeof value === "object" && value !== null;
}

export function hasSafePrototype(value: object): boolean {
	const prototype = Object.getPrototypeOf(value);
	return prototype === Object.prototype || prototype === null;
}

export function isRuntimePublicEnvKey(key: string): key is RuntimePublicEnvKey {
	return (
		key === "PAYPAL_CLIENT_ID" ||
		key.startsWith("BUN_PUBLIC_") ||
		key.startsWith("PUBLIC_")
	);
}

export const RuntimePublicEnvKeySchema = Schema.String.pipe(
	Schema.filter(isRuntimePublicEnvKey, {
		description: "runtime public env key",
	}),
);

export const RuntimePublicEnvSchema = Schema.Record({
	key: RuntimePublicEnvKeySchema,
	value: Schema.String,
});

function collectRuntimePublicEnvCandidate(source: unknown): Record<string, string> | null {
	if (!isObjectRecord(source) || !hasSafePrototype(source)) {
		return null;
	}

	const candidate = Object.create(null) as Record<string, string>;

	for (const key of Object.getOwnPropertyNames(source)) {
		if (!isRuntimePublicEnvKey(key)) {
			continue;
		}

		const descriptor = Object.getOwnPropertyDescriptor(source, key);
		if (!descriptor || !("value" in descriptor) || typeof descriptor.value !== "string") {
			continue;
		}

		candidate[key] = descriptor.value;
	}

	return candidate;
}

function freezeRuntimePublicEnv(value: Readonly<Record<string, string>>): Readonly<RuntimePublicEnv> {
	const sanitized = Object.create(null) as RuntimePublicEnv;

	for (const key of Object.getOwnPropertyNames(value)) {
		if (isRuntimePublicEnvKey(key)) {
			sanitized[key] = value[key];
		}
	}

	return Object.freeze(sanitized) as Readonly<RuntimePublicEnv>;
}

export function decodeRuntimePublicEnv(
	source: unknown,
): Either.Either<Readonly<RuntimePublicEnv>, unknown> {
	const candidate = collectRuntimePublicEnvCandidate(source);
	if (!candidate) {
		return Either.right(EMPTY_RUNTIME_PUBLIC_ENV);
	}

	const decoded = Schema.decodeUnknownEither(RuntimePublicEnvSchema)(candidate);
	if (Either.isLeft(decoded)) {
		return Either.left(decoded.left);
	}

	return Either.right(freezeRuntimePublicEnv(decoded.right));
}

export function sanitizeRuntimePublicEnv(source: unknown): Readonly<RuntimePublicEnv> {
	const decoded = decodeRuntimePublicEnv(source);
	return Either.isRight(decoded) ? decoded.right : EMPTY_RUNTIME_PUBLIC_ENV;
}
